/**
 * What MAHI says while a try-on renders.
 *
 * The render takes 18-30 seconds. That is far too long for a spinner: a
 * progress indicator with nothing to read turns a wait into a question about
 * whether anything is happening. Ported from the Shopify plugin's try-on
 * flow, which solved the same problem with two tracks of copy — a status line
 * that reports what is happening, and a longer note that is simply worth
 * reading.
 *
 * Rewritten into MAHI's voice rather than lifted verbatim. The plugin is an
 * embedded widget with no persona; she is a stylist talking to someone whose
 * photo she is currently working with, and "Generating your look…" is a
 * machine describing itself.
 */

/** The status line. Reports the actual stage, in order, then holds. */
export const TRYON_STAGES = [
  'Looking at how this is cut…',
  'Putting it on you…',
  'Getting the drape right…',
  'Almost there - the details take a moment…',
];

/**
 * The "while you wait" note. Longer, and about fashion rather than about the
 * render — the point is to be worth reading, not to fill a gap.
 *
 * Kept from the plugin nearly as-is: these were written to be true and
 * genuinely interesting, and rewriting them for voice would have cost the
 * facts. Order is fixed, and the starting index is randomised per render so
 * someone who tries on three things does not read the same opening twice.
 */
export const STYLE_NOTES = [
  'The little black dress was born in 1926 - Vogue predicted the whole world would wear it. They were right.',
  'Your eyes judge fit in about 0.3 seconds. Trust your first instinct when the result appears.',
  'Monochrome outfits make you look taller - a stylist’s favourite quiet trick.',
  'Denim was invented for gold miners. It took a century to reach the runway.',
  'V-necks and vertical seams can visually add up to 5cm of height.',
  'The word “jeans” comes from Genoa - sailors there wore “bleu de Gênes”.',
  'High heels were first worn by Persian horsemen, long before they hit a catwalk.',
  'Most people wear 20% of their wardrobe 80% of the time. Choose that 20% well.',
  'A well-fitted shoulder seam flatters more than any other measurement.',
  'Buttons sit on opposite sides for men and women - a leftover from the Victorian era.',
  'Silk thread is stronger than a steel strand of the same thickness.',
  'Sneakers with dresses were considered scandalous until the 1990s.',
  'Screen light can shift how a colour reads by up to 30% - check fabric tones in daylight.',
  'The average garment travels farther before reaching you than most people do in a year.',
];

// What the fake progress bar is calibrated against. Real try-ons land around
// 18-30s (tryon then size, sequential — see routes/mira/tryon.py), so the bar
// is paced to the optimistic end and then holds, which reads as "nearly
// there" rather than as a bar that finished and lied.
export const EXPECTED_SECONDS = 18;
